import React from "react";

// Subtle celestial backdrop: zodiac wheel, constellations, sacred geometry, sigils.
// Fine gold linework at very low opacity, sits behind all content.
const stroke = "rgba(194,164,107,0.9)";

const ZodiacWheel = ({ className = "" }) => (
  <svg viewBox="0 0 400 400" className={className} fill="none">
    <g stroke={stroke} strokeWidth="0.8">
      <circle cx="200" cy="200" r="190" />
      <circle cx="200" cy="200" r="150" />
      <circle cx="200" cy="200" r="120" />
      <circle cx="200" cy="200" r="60" />
      {[...Array(12)].map((_, i) => {
        const a = (i * 30 * Math.PI) / 180;
        return (
          <line
            key={i}
            x1={200 + 120 * Math.cos(a)}
            y1={200 + 120 * Math.sin(a)}
            x2={200 + 190 * Math.cos(a)}
            y2={200 + 190 * Math.sin(a)}
          />
        );
      })}
      {[...Array(72)].map((_, i) => {
        const a = (i * 5 * Math.PI) / 180;
        return (
          <line
            key={`t${i}`}
            x1={200 + 150 * Math.cos(a)}
            y1={200 + 150 * Math.sin(a)}
            x2={200 + 156 * Math.cos(a)}
            y2={200 + 156 * Math.sin(a)}
          />
        );
      })}
    </g>
    {/* inner star */}
    <g stroke={stroke} strokeWidth="0.7">
      {[...Array(6)].map((_, i) => {
        const a = (i * 60 * Math.PI) / 180;
        return (
          <line key={`s${i}`} x1="200" y1="200" x2={200 + 60 * Math.cos(a)} y2={200 + 60 * Math.sin(a)} />
        );
      })}
    </g>
  </svg>
);

const Constellation = ({ points, className = "" }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none">
    <polyline points={points.map((p) => p.join(",")).join(" ")} stroke={stroke} strokeWidth="0.7" />
    {points.map((p, i) => (
      <circle key={i} cx={p[0]} cy={p[1]} r={i % 3 === 0 ? 2.2 : 1.4} fill={stroke} />
    ))}
  </svg>
);

const SeedOfLife = ({ className = "" }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none">
    <g stroke={stroke} strokeWidth="0.8">
      <circle cx="100" cy="100" r="40" />
      {[...Array(6)].map((_, i) => {
        const a = (i * 60 * Math.PI) / 180;
        return <circle key={i} cx={100 + 40 * Math.cos(a)} cy={100 + 40 * Math.sin(a)} r="40" />;
      })}
    </g>
  </svg>
);

const Sun = ({ className = "" }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none">
    <g stroke={stroke} strokeWidth="0.8">
      <circle cx="100" cy="100" r="34" />
      {[...Array(16)].map((_, i) => {
        const a = (i * 22.5 * Math.PI) / 180;
        return (
          <line
            key={i}
            x1={100 + 44 * Math.cos(a)}
            y1={100 + 44 * Math.sin(a)}
            x2={100 + (i % 2 ? 66 : 56) * Math.cos(a)}
            y2={100 + (i % 2 ? 66 : 56) * Math.sin(a)}
          />
        );
      })}
    </g>
  </svg>
);

export const CelestialBackground = () => {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true" data-testid="celestial-bg">
      <ZodiacWheel className="absolute -right-24 top-10 w-[520px] h-[520px] opacity-[0.07] animate-[spin_180s_linear_infinite]" />
      <SeedOfLife className="absolute -left-20 top-1/3 w-[360px] h-[360px] opacity-[0.06]" />
      <Sun className="absolute right-1/4 bottom-24 w-[220px] h-[220px] opacity-[0.06]" />
      <Constellation
        points={[[20, 40], [60, 70], [95, 55], [130, 95], [170, 80]]}
        className="absolute left-[8%] bottom-[18%] w-[260px] h-[260px] opacity-[0.10]"
      />
      <Constellation
        points={[[30, 150], [70, 120], [90, 60], [140, 40], [160, 110]]}
        className="absolute right-[10%] top-[22%] w-[240px] h-[240px] opacity-[0.09]"
      />
      <Constellation
        points={[[20, 20], [60, 60], [100, 40], [140, 100], [120, 150]]}
        className="absolute left-[42%] top-[6%] w-[200px] h-[200px] opacity-[0.07]"
      />
      {/* scattered stars */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.12]">
        {[...Array(40)].map((_, i) => (
          <circle
            key={i}
            cx={`${(i * 53) % 100}%`}
            cy={`${(i * 71) % 100}%`}
            r={i % 6 === 0 ? 1.6 : 0.9}
            fill={stroke}
          />
        ))}
      </svg>
    </div>
  );
};

export default CelestialBackground;
